/**
 * OpenFileDialog — Open mode of the host file dialogs.
 *
 * Browses Documents, Arche Saves and Recent (never My-Fias), with a selection
 * that can span folders (a removable tray), a preview pane, type filtering
 * from the requester's accepted types (the rest hidden, or shown disabled on
 * request), and upload from the device into the current Documents folder.
 *
 * One workspace per pick: a grant is made on the workspace the files live in,
 * so while the tray holds files, switching workspace waits for it to be
 * cleared.
 *
 * Used directly by internal surfaces, as the picker step of a plugin's pick
 * (with an access line saying what the arche is asking for), and — through
 * `VaultFilePickerDialog` — by every older picker preset.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Upload, X } from 'lucide-react';
import { api, type VaultFileDialogDocumentDto } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { uploadLocalFileToVault } from '@/lib/vault-upload';
import { captureError } from '@/lib/telemetry';
import { mimeToFileKind } from '@/lib/vault-file-kinds';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { FileDialogShell } from './FileDialogShell';
import type { FileListEntry } from './FileList';
import { scrubForDisplay } from './display-safety';
import { formatBytes, formatModified, iconForMime, typeLabel } from './format';
import { describeFolderPath } from './locations';
import { useFileBrowser, type FileAccept } from './useFileBrowser';

export interface OpenFileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  /** Default false. */
  multiSelect?: boolean;
  /** Cap on the tray. `0` disables choosing (the caller is already full). */
  selectionLimit?: number;
  selectionLimitMessage?: string;
  confirmLabel?: string;
  /** The requester's accepted types. */
  accept?: FileAccept;
  /** An extra client-side predicate (legacy `mimeFilter` functions). */
  filter?: (file: VaultFileDialogDocumentDto) => boolean;
  /** Default true. Uploads go into the current Documents folder. */
  allowUpload?: boolean;
  uploadAccept?: string;
  multiUpload?: boolean;
  /** Per-file cap on uploads (bytes). Default 50 MB. */
  maxUploadSize?: number;
  validateUpload?: (file: File) => string | null;
  /** The acting workspace; `null` = Personal. */
  initialHolderId?: string | null;
  /** Pin the dialog to `initialHolderId` (the caller's records live there). */
  lockHolder?: boolean;
  /** Offer only the Personal workspace. */
  personalOnly?: boolean;
  /** A pick whose result an arche receives — never proprietary rows. */
  forArche?: boolean;
  /** Last-location memory key (an arche id or surface name). */
  memoryKey?: string;
  /** What the requesting arche is asking for (plugin picks). */
  accessLine?: ReactNode;
  /** Render inline (inside a surface's own modal). */
  embedded?: boolean;
  onConfirm: (files: VaultFileDialogDocumentDto[]) => void | Promise<void>;
}

const DEFAULT_MAX_UPLOAD = 50 * 1024 * 1024;

function defaultLimitMessage(limit: number): string {
  if (limit <= 0) return "You've already selected as many files as this allows.";
  return `You can select at most ${limit} file${limit === 1 ? '' : 's'}.`;
}

export function OpenFileDialog(props: OpenFileDialogProps) {
  const qc = useQueryClient();
  const multiSelect = props.multiSelect === true;
  const [selected, setSelected] = useState<Map<string, VaultFileDialogDocumentDto>>(new Map());
  const [showRejected, setShowRejected] = useState(false);
  const [focused, setFocused] = useState<FileListEntry | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const uploadRef = useRef<HTMLInputElement>(null);

  const areas = useMemo(() => ['documents' as const, 'arche-saves' as const], []);
  const holderLockedTo =
    props.lockHolder && props.initialHolderId
      ? props.initialHolderId
      : selected.size > 0
        ? ([...selected.values()][0]?.holderId ?? null)
        : null;
  const browser = useFileBrowser({
    open: props.open,
    need: 'read',
    areas,
    initialHolderId: props.initialHolderId ?? null,
    memoryKey: props.memoryKey,
    accept: props.accept,
    filter: props.filter,
    showRejected,
    forArche: props.forArche,
    lockedHolderId: holderLockedTo,
    personalOnly: props.personalOnly,
  });

  useEffect(() => {
    if (!props.open) return;
    setSelected(new Map());
    setShowRejected(false);
    setFocused(null);
    setBusy(false);
  }, [props.open]);

  // Single-select never carries a hidden selection across views.
  const locationKey = JSON.stringify(browser.location) + browser.isSearching;
  useEffect(() => {
    if (!multiSelect) setSelected(new Map());
  }, [locationKey, multiSelect]);

  const extraFilter = props.filter;
  const passes = useCallback(
    (file: VaultFileDialogDocumentDto) => !extraFilter || extraFilter(file),
    [extraFilter],
  );

  const limitMessage = () =>
    props.selectionLimitMessage ?? defaultLimitMessage(props.selectionLimit ?? 0);

  const toggle = (file: VaultFileDialogDocumentDto, opts: { toggle: boolean }) => {
    if (!passes(file)) return;
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(file.documentId)) {
        if (opts.toggle || multiSelect) next.delete(file.documentId);
        return next;
      }
      if (!multiSelect) {
        next.clear();
        next.set(file.documentId, file);
        return next;
      }
      if (props.selectionLimit !== undefined && next.size >= props.selectionLimit) {
        toast({
          title: 'Selection limit reached',
          description: limitMessage(),
          variant: 'destructive',
        });
        return next;
      }
      next.set(file.documentId, file);
      return next;
    });
  };

  const confirm = async (files: VaultFileDialogDocumentDto[]) => {
    if (files.length === 0) return;
    if (props.selectionLimit !== undefined && props.selectionLimit <= 0) {
      toast({
        title: 'Selection limit reached',
        description: limitMessage(),
        variant: 'destructive',
      });
      return;
    }
    setBusy(true);
    try {
      browser.rememberLocation();
      await props.onConfirm(files);
    } catch (err) {
      toast({
        title: 'That didn’t work',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  // Uploads land in the Documents folder on screen; nowhere else accepts them.
  const uploadFolderId =
    browser.location.kind === 'folder' &&
    browser.location.area === 'documents' &&
    !browser.isSearching
      ? browser.location.folderId
      : undefined;
  const canUpload =
    props.allowUpload !== false && uploadFolderId !== undefined && !!browser.holderId;
  const uploadDisabledReason =
    props.allowUpload === false
      ? null
      : uploadFolderId === undefined
        ? 'Open a folder in Documents to upload into it.'
        : null;

  const handleUploads = async (files: File[]) => {
    if (!browser.holderId || uploadFolderId === undefined) return;
    const maxSize = props.maxUploadSize ?? DEFAULT_MAX_UPLOAD;
    const rejected: string[] = [];
    const accepted: File[] = [];
    for (const file of files) {
      if (file.size > maxSize) rejected.push(`${file.name} (over ${formatBytes(maxSize)})`);
      else {
        const problem = props.validateUpload?.(file);
        if (problem) rejected.push(`${file.name} — ${problem}`);
        else accepted.push(file);
      }
    }
    if (rejected.length > 0) {
      toast({
        title: rejected.length === files.length ? 'Upload rejected' : 'Some files skipped',
        description:
          rejected.slice(0, 3).join(', ') +
          (rejected.length > 3 ? ` +${rejected.length - 3} more` : ''),
        variant: 'destructive',
      });
    }
    if (accepted.length === 0) return;
    setUploading(true);
    const failed: string[] = [];
    const folderPath = uploadFolderId
      ? (browser.folders.find((f) => f.folderId === uploadFolderId)?.path ?? null)
      : null;
    for (const file of accepted) {
      try {
        const { document } = await uploadLocalFileToVault(file, browser.holderId, {
          folderId: uploadFolderId,
        });
        const row: VaultFileDialogDocumentDto = { ...document, folderPath, sourceArcheName: null };
        setSelected((prev) => {
          const next = new Map(prev);
          if (!multiSelect) next.clear();
          else if (props.selectionLimit !== undefined && next.size >= props.selectionLimit)
            return next;
          next.set(row.documentId, row);
          return next;
        });
      } catch (err) {
        failed.push(file.name);
        captureError(err instanceof Error ? err : new Error(String(err)), {
          source: 'openFileDialog.upload',
          fileName: file.name,
        });
      }
    }
    setUploading(false);
    void qc.invalidateQueries({ queryKey: queryKeys.vaultDocuments.forHolder(browser.holderId) });
    if (failed.length > 0) {
      toast({
        title: 'Upload failed',
        description: failed.slice(0, 3).join(', '),
        variant: 'destructive',
      });
    }
  };

  const selectedFiles = [...selected.values()];
  const confirmLabel =
    props.confirmLabel ??
    (multiSelect
      ? selectedFiles.length > 0
        ? `Add ${selectedFiles.length} ${selectedFiles.length === 1 ? 'file' : 'files'}`
        : 'Add files'
      : 'Open');

  const hasAccept = !!(
    props.accept?.kinds?.length ||
    props.accept?.mimeTypes?.length ||
    props.accept?.extensions?.length
  );

  const toolbarExtra =
    props.allowUpload === false ? null : (
      <>
        <Button
          variant="outline"
          size="sm"
          className="h-10"
          disabled={!canUpload || uploading || busy}
          title={uploadDisabledReason ?? 'Upload from this device'}
          onClick={() => uploadRef.current?.click()}
        >
          {uploading ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Upload className="mr-1.5 h-4 w-4" aria-hidden="true" />
          )}
          Upload
        </Button>
        <input
          ref={uploadRef}
          type="file"
          className="hidden"
          accept={props.uploadAccept}
          multiple={props.multiUpload === true}
          aria-label="Upload files"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = '';
            if (files.length > 0) void handleUploads(files);
          }}
        />
      </>
    );

  const banner = hasAccept ? (
    <label className="flex items-center gap-2 border-b px-3 py-1.5 text-xs text-muted-foreground">
      <input
        type="checkbox"
        checked={showRejected}
        onChange={(e) => setShowRejected(e.target.checked)}
      />
      Show all files (others can’t be opened here)
    </label>
  ) : null;

  const footer = (
    <div className="space-y-2">
      {multiSelect && selectedFiles.length > 0 && (
        <ul aria-label="Selected files" className="flex max-h-20 flex-wrap gap-1.5 overflow-y-auto">
          {selectedFiles.map((file) => (
            <li
              key={file.documentId}
              className="flex max-w-[14rem] items-center gap-1 rounded-full border bg-muted/40 py-0.5 pl-2.5 pr-1 text-xs"
            >
              <span className="truncate">{scrubForDisplay(file.name, 80)}</span>
              <button
                type="button"
                className="rounded-full p-1 hover:bg-muted"
                aria-label={`Remove ${scrubForDisplay(file.name, 80)}`}
                onClick={() => toggle(file, { toggle: true })}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {props.accessLine && <div className="text-xs text-muted-foreground">{props.accessLine}</div>}
      <div className="flex items-center justify-end gap-2">
        {multiSelect && selectedFiles.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="mr-auto"
            onClick={() => setSelected(new Map())}
          >
            Clear selection
          </Button>
        )}
        {!props.embedded && (
          <Button variant="outline" disabled={busy} onClick={() => props.onOpenChange(false)}>
            Cancel
          </Button>
        )}
        <Button
          disabled={selectedFiles.length === 0 || busy}
          onClick={() => void confirm(selectedFiles)}
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          {confirmLabel}
        </Button>
      </div>
    </div>
  );

  return (
    <FileDialogShell
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={props.title ?? 'Open a file'}
      description={props.description}
      browser={browser}
      showArcheSaves
      chipKinds={showRejected ? undefined : props.accept?.kinds}
      selectedIds={new Set(selected.keys())}
      multiSelect={multiSelect}
      onSelectFile={toggle}
      onActivateFile={(file) => {
        if (!passes(file)) return;
        // "Open this one now" — in multi-select too, the gesture reads as
        // choosing this file rather than adding to the tray.
        void confirm([file]);
      }}
      onFocusEntry={setFocused}
      toolbarExtra={toolbarExtra}
      banner={banner}
      aside={<PreviewPane entry={focused} thumbnails={browser.thumbnails} />}
      footer={footer}
      busy={busy}
      emptyHint={canUpload ? 'Upload a file to get started.' : undefined}
      workspaceLockedReason={
        props.lockHolder
          ? 'Files come from this workspace.'
          : selected.size > 0
            ? 'Clear your selection to choose from another workspace.'
            : null
      }
      embedded={props.embedded}
    />
  );
}

/** The right-hand pane: a larger look at the focused file, and its facts. */
function PreviewPane(props: {
  entry: FileListEntry | null;
  thumbnails: ReadonlyMap<string, string>;
}) {
  const file = props.entry?.kind === 'file' ? props.entry.file : null;
  const text = useTextPreview(file);
  if (!props.entry) {
    return <p className="text-xs text-muted-foreground">Select a file to preview it.</p>;
  }
  if (props.entry.kind === 'folder') {
    return (
      <div className="space-y-1 text-sm">
        <div className="font-medium">{props.entry.name}</div>
        <div className="text-xs text-muted-foreground">Folder</div>
      </div>
    );
  }
  const f = props.entry.file;
  const thumb = props.thumbnails.get(f.documentId);
  const Icon = iconForMime(f.mimeType);
  return (
    <div className="space-y-3 text-sm">
      <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border bg-muted/30">
        {thumb ? (
          <img src={thumb} alt="" className="h-full w-full object-contain" />
        ) : text ? (
          <pre className="h-full w-full overflow-hidden whitespace-pre-wrap p-2 text-[10px] leading-snug text-muted-foreground">
            {text}
          </pre>
        ) : (
          <Icon className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
        )}
      </div>
      <div className="break-words font-medium">{scrubForDisplay(f.name, 255)}</div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-xs">
        <dt className="text-muted-foreground">Type</dt>
        <dd>{typeLabel(f.name, f.mimeType)}</dd>
        <dt className="text-muted-foreground">Size</dt>
        <dd>{formatBytes(f.sizeBytes)}</dd>
        <dt className="text-muted-foreground">Modified</dt>
        <dd>{formatModified(f.updatedAt)}</dd>
        <dt className="text-muted-foreground">Where</dt>
        <dd>{describeFolderPath(f.folderPath)}</dd>
        {f.sourceArcheName && (
          <>
            <dt className="text-muted-foreground">Made by</dt>
            <dd>{f.sourceArcheName}</dd>
          </>
        )}
        {f.lastWrittenByArcheId && (
          <>
            <dt className="text-muted-foreground">Last changed</dt>
            <dd>by an arche{f.lastWrittenAt ? `, ${formatModified(f.lastWrittenAt)}` : ''}</dd>
          </>
        )}
      </dl>
    </div>
  );
}

const TEXT_PREVIEW_BYTES = 2048;

/** The first lines of a text file — fetched on demand, a small range only. */
function useTextPreview(file: VaultFileDialogDocumentDto | null): string | null {
  const [text, setText] = useState<string | null>(null);
  const id = file?.documentId ?? null;
  const isText =
    !!file &&
    (file.mimeType?.startsWith('text/') ||
      mimeToFileKind(file.mimeType) === 'code' ||
      file.mimeType === 'application/json');
  useEffect(() => {
    setText(null);
    if (!id || !isText) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const { url } = await api.getVaultDocumentDownloadUrl(id);
          const res = await fetch(url, { headers: { Range: `bytes=0-${TEXT_PREVIEW_BYTES - 1}` } });
          if (!res.ok || cancelled) return;
          const body = await res.text();
          if (!cancelled) setText(body.slice(0, TEXT_PREVIEW_BYTES));
        } catch {
          // A preview is a convenience; the file is still choosable.
        }
      })();
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [id, isText]);
  return text;
}
